import { useSetAtom } from 'jotai';
import { useCallback } from 'react';
import { useNavigate } from 'react-router';
import { upperFirst } from 'lodash-es';
import { useLoadingBarState } from '../../../../../components/LoadingBar/LoadingBarContext';
import { useToastMessageState } from '../../../../../components/ToastMessage/ToastMessageProvider';
import { PersonaKey } from '../../../../../services/PermissionAndRolesKeys';
import { PermissionService } from '../../../../../services/PermissionService';
import { getErrorMessage } from '../../../../../services/queryHelpers';
import { LoadingId } from '../../../../../types';
import { deleteKPIRequest, sendKPIRequests } from '../../../../../services/queries/KPIRequestsQueries';
import { ROUTES } from '../../../../../constants/RouteNameMapping';

import { useInvalidateKPIRequests } from '../KPIRequestResponse/hooks/useInvalidateKPIRequests';
import { pluralizeWord } from '../../../../../util/stringUtils';
import {
  kpiRequestErrorModalOpenState,
  kpiRequestsNotSentState,
} from '../../../../../services/state/KPI/KPIRequestsStateJ';

const ROUTE_IN_PROGRESS_TAB = `/${ROUTES.KPI}/${ROUTES.KPI_REQUESTS}/${ROUTES.KPI_REQUESTS_IN_PROGRESS}`;

type SendKPIRequestPayload = {
  requestsIds: number[];
  totalCompanies?: number;
  sendReminder?: boolean;
};

export function useKPIRequestsNotSentActions() {
  const { actions } = useLoadingBarState();
  const { pushErrorToast, pushSuccessToast } = useToastMessageState();
  const navigate = useNavigate();
  const invalidateRequests = useInvalidateKPIRequests();
  const setNotSent = useSetAtom(kpiRequestsNotSentState);
  const setErrorModalOpen = useSetAtom(kpiRequestErrorModalOpenState);

  const sendKPIRequestAction = useCallback(
    async (payload: SendKPIRequestPayload) => {
      const { requestsIds, totalCompanies = 1, sendReminder = false } = payload;
      actions.startLoading(LoadingId.sendKPIRequests);

      try {
        const response = await sendKPIRequests(requestsIds);
        await invalidateRequests();
        if (response.data?.requestIds?.length) {
          pushErrorToast({
            message: response.data.message,
            buttonLabel: 'Go To Details',
            buttonAction: () => {
              setNotSent(response.data.requestIds);
              setErrorModalOpen(true);
            },
          });
          return;
        }
        const successMsg = `${upperFirst(
          pluralizeWord('request', requestsIds.length)
        )} for ${totalCompanies} ${pluralizeWord('company', totalCompanies)} ${pluralizeWord('was', totalCompanies)} successfully sent to ${pluralizeWord('respondent', requestsIds.length)}`;

        const actionProps = sendReminder
          ? {}
          : {
              buttonLabel: 'Go to In Progress',
              buttonAction: () => {
                navigate(ROUTE_IN_PROGRESS_TAB);
              },
            };

        pushSuccessToast({
          title: `The ${pluralizeWord('request', requestsIds.length)} ${pluralizeWord('was', requestsIds.length)} sent`,
          message: successMsg,
          ...actionProps,
        });

        return response;
      } catch (err) {
        const message = getErrorMessage(err, `Failed to send`);
        pushErrorToast({ message });
      } finally {
        actions.stopLoading(LoadingId.sendKPIRequests);
      }
    },
    [actions, invalidateRequests, navigate, pushErrorToast, pushSuccessToast, setErrorModalOpen, setNotSent]
  );

  const deleteKPIRequestAction = useCallback(
    async (requestId: number) => {
      actions.startLoading(LoadingId.deleteKPIRequest);

      try {
        const response = await deleteKPIRequest(requestId);

        invalidateRequests();

        const successMsg = `Request was deleted successfully`;
        // Managed-service users have no access to the Deleted Requests tab.
        const actionProps = PermissionService.get().hasPersona(PersonaKey.managedServices)
          ? {}
          : {
              buttonLabel: 'Go to Deleted',
              buttonAction: () =>
                navigate(`/${ROUTES.KPI}/${ROUTES.KPI_REQUESTS}/${ROUTES.KPI_REQUESTS_ARCHIVED}`),
            };

        pushSuccessToast({
          message: successMsg,
          ...actionProps,
        });

        return response;
      } catch (err) {
        console.error(err);
        const message = getErrorMessage(err, `Failed to delete request`);
        pushErrorToast({ message });
      } finally {
        actions.stopLoading(LoadingId.deleteKPIRequest);
      }
    },
    [actions, invalidateRequests, navigate, pushErrorToast, pushSuccessToast]
  );

  return {
    deleteKPIRequestAction,
    invalidateRequests,
    sendKPIRequestAction,
  };
}
